
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import { useEffect } from 'react';

export default function BloubergstrandPage() {
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Bloubergstrand Painters Cape Town | Professional Coastal Painting Services | Marine Grade Protection";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert painting services for Bloubergstrand properties. Specialized coastal painting with marine-grade protection against salt air. Professional interior & exterior painting in Bloubergstrand Cape Town.');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'Bloubergstrand painters, coastal painting services, marine grade protection, Bloubergstrand property painting, Cape Town painters');
    }
    
    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bloubergstrand`);
    }
    
    // Add Schema.org JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Bloubergstrand Painters - Professional Coastal Painting Services",
      "description": "Expert painting services for Bloubergstrand properties with specialized coastal protection and marine-grade materials.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/bloubergstrand`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Cape Town Painters - Bloubergstrand",
        "description": "Professional coastal painting services for Bloubergstrand with marine-grade protection against salt air.",
        "areaServed": "Bloubergstrand, Cape Town",
        "serviceType": "Coastal Property Painting Services"
      }
    });
    document.head.appendChild(script);
    
    return () => {
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript && existingScript.textContent?.includes('Bloubergstrand')) {
        existingScript.remove();
      }
    };
  }, []);

  const services = [
    {
      icon: 'ri-home-line',
      title: 'Beach House Painting',
      description: 'Specialized painting for Bloubergstrand beach houses and coastal properties with wind-resistant finishes.',
      price: 'From R10,000',
      features: ['Wind-resistant coatings', 'Salt air protection', 'UV resistance', 'Color consultation']
    },
    {
      icon: 'ri-building-line',
      title: 'Holiday Home Maintenance',
      description: 'Regular maintenance painting for Bloubergstrand holiday homes and rental properties with flexible scheduling.',
      price: 'From R6,000',
      features: ['Scheduled maintenance', 'Rental coordination', 'Quick turnaround', 'Tenant-friendly service']
    },
    {
      icon: 'ri-brush-line',
      title: 'Wind Protection Coating',
      description: "Specialized coatings to protect against Bloubergstrand's strong coastal winds and sand abrasion.",
      price: 'From R8,000',
      features: ['Abrasion resistance', 'Flexible finishes', 'Sand protection', 'Long-term durability']
    },
    {
      icon: 'ri-shield-line',
      title: 'Marine Grade Protection',
      description: 'Advanced marine-grade coatings specifically designed for Bloubergstrand\'s harsh coastal environment.',
      price: 'From R12,000',
      features: ['Corrosion resistance', 'Moisture barriers', 'Salt protection', 'Extended warranties']
    },
    {
      icon: 'ri-palette-line',
      title: 'Coastal Color Design',
      description: 'Expert color consultation services tailored to Bloubergstrand\'s unique coastal aesthetic and lighting.',
      price: 'From R2,500',
      features: ['Coastal color schemes', 'Light analysis', 'Trend consultation', 'Sample coordination']
    },
    {
      icon: 'ri-tools-line',
      title: 'Restoration Services',
      description: 'Specialized restoration for older Bloubergstrand properties requiring careful attention to coastal damage.',
      price: 'From R9,000',
      features: ['Damage assessment', 'Surface restoration', 'Protective treatments', 'Heritage preservation']
    }
  ];

  const testimonials = [
    {
      name: 'Peter van der Merwe',
      property: 'Beachfront Holiday Home',
      rating: 5,
      text: 'Excellent work on our Bloubergstrand holiday home. The paint has held up beautifully against the wind and salt. The team was professional and understood our rental schedule perfectly.',
      image: 'https://public.readdy.ai/ai/img_res/c18f6cd8eeca75f7150f63f2df6fa7a2.jpg'
    },
    {
      name: 'Susan Mitchell',
      property: 'Coastal Family Home',
      rating: 5,
      text: 'Outstanding service and quality. They understood the unique challenges of painting in Bloubergstrand and provided excellent advice on wind-resistant finishes.',
      image: 'https://readdy.ai/api/search-image?query=Happy%20woman%20homeowner%20in%20Bloubergstrand%20coastal%20home%20with%20Table%20Mountain%20views%20through%20window%2C%20professionally%20painted%20interior%2C%20satisfied%20client%20portrait%2C%20beachside%20location&width=80&height=80&seq=blouberg2new&orientation=squarish'
    },
    {
      name: 'Johan Steyn',
      property: 'Beachside Villa',
      rating: 5,
      text: 'Exceptional results on our Bloubergstrand villa. The marine-grade coating has protected our property excellently against the harsh coastal conditions and strong winds.',
      image: 'https://readdy.ai/api/search-image?query=Professional%20man%20homeowner%20at%20Bloubergstrand%20beachside%20villa%20with%20Table%20Mountain%20view%2C%20newly%20painted%20exterior%2C%20satisfied%20customer%2C%20windsurfing%20beach%20location&width=80&height=80&seq=blouberg5new&orientation=squarish'
    }
  ];

  const projects = [
    {
      title: 'Beachfront Villa Complex',
      description: 'Complete exterior painting with wind-resistant coatings and coordinated color scheme across multiple properties',
      image: 'https://readdy.ai/api/search-image?query=Beachfront%20villa%20complex%20in%20Bloubergstrand%20with%20Table%20Mountain%20backdrop%20across%20the%20bay%2C%20freshly%20painted%20exteriors%2C%20windsurfing%20beach%2C%20coastal%20architecture%2C%20professional%20painting%20work&width=400&height=300&seq=blouberg3new&orientation=landscape'
    },
    {
      title: 'Coastal Holiday Homes',
      description: 'Interior and exterior renovation for rental properties with durable finishes and quick turnaround times',
      image: 'https://readdy.ai/api/search-image?query=Row%20of%20coastal%20holiday%20homes%20in%20Bloubergstrand%20with%20Table%20Mountain%20view%2C%20newly%20painted%20exteriors%2C%20beachside%20location%2C%20windsurfing%20area%2C%20modern%20residential%20architecture&width=400&height=300&seq=blouberg4new&orientation=landscape'
    },
    {
      title: 'Windsurfing Lodge Renovation',
      description: 'Comprehensive painting and protection project for popular Bloubergstrand accommodation facility',
      image: 'https://readdy.ai/api/search-image?query=Bloubergstrand%20windsurfing%20lodge%20with%20professional%20exterior%20painting%2C%20Table%20Mountain%20backdrop%2C%20coastal%20accommodation%2C%20windsurfing%20equipment%20visible%2C%20beachfront%20location&width=400&height=300&seq=blouberg6new&orientation=landscape'
    },
    {
      title: 'Residential Estate Maintenance',
      description: 'Large-scale maintenance program for Bloubergstrand residential estate with specialized coastal protection',
      image: 'https://readdy.ai/api/search-image?query=Bloubergstrand%20residential%20estate%20with%20freshly%20maintained%20painted%20exteriors%2C%20Table%20Mountain%20view%2C%20coastal%20development%2C%20windsurfing%20beach%20nearby%2C%20professional%20maintenance%20work&width=400&height=300&seq=blouberg7new&orientation=landscape'
    }
  ];

  const paintingProcess = [
    {
      step: '1',
      title: 'Wind Assessment',
      description: 'Comprehensive evaluation of wind exposure and coastal conditions specific to your Bloubergstrand property location.'
    },
    {
      step: '2',
      title: 'Protection Planning',
      description: 'Development of specialized protection strategies considering Bloubergstrand\'s unique wind and salt air challenges.'
    },
    {
      step: '3',
      title: 'Surface Preparation',
      description: 'Thorough preparation including wind damage repair, cleaning, and specialized priming for coastal conditions.'
    },
    {
      step: '4',
      title: 'Weather-Resistant Application',
      description: 'Professional application of wind and salt-resistant coatings using techniques proven in Bloubergstrand conditions.'
    },
    {
      step: '5',
      title: 'Quality Assurance',
      description: 'Comprehensive inspection and testing to ensure optimal performance in Bloubergstrand\'s challenging environment.'
    },
    {
      step: '6',
      title: 'Maintenance Program',
      description: 'Customized maintenance recommendations and scheduling to protect your investment against coastal elements.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative bg-cover bg-center bg-no-repeat min-h-screen flex items-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url('https://readdy.ai/api/search-image?query=Professional%20painters%20working%20on%20beachfront%20property%20in%20Bloubergstrand%20with%20iconic%20Table%20Mountain%20view%20across%20the%20bay%2C%20windsurfing%20conditions%2C%20painting%20equipment%2C%20coastal%20home%20renovation%2C%20sunny%20windy%20day&width=1920&height=1080&seq=blouberghero2&orientation=landscape')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Professional Painters Bloubergstrand
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 leading-relaxed">
              Expert painting services for Bloubergstrand beach houses and coastal properties. Wind-resistant coatings and specialized finishes for your seaside home with protection against the area's unique coastal challenges.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/services"
                className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200"
              >
                Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Bloubergstrand Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Specialized Painting for Bloubergstrand Properties
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Bloubergstrand is renowned as one of Cape Town's premier coastal destinations, famous for its spectacular views of Table Mountain, world-class windsurfing conditions, and vibrant beachside lifestyle. This popular area attracts both permanent residents and holiday home owners who appreciate the unique combination of natural beauty and recreational opportunities.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                The coastal location of Bloubergstrand presents exceptional challenges for property maintenance, particularly when it comes to exterior painting and protective coatings. The area is known for its strong, persistent winds that carry salt spray and sand, creating an environment that can quickly deteriorate standard paint finishes and building materials.
              </p>
              <p className="text-lg text-gray-600">
                Our specialized team has developed extensive expertise in addressing Bloubergstrand's unique environmental challenges. We understand the specific requirements for different property types in the area, from beachfront holiday homes to residential estates, and provide tailored solutions that ensure long-lasting protection and aesthetic appeal in this demanding coastal environment.
              </p>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Bloubergstrand%20beachfront%20with%20Table%20Mountain%20view%20across%20the%20bay%2C%20windsurfing%20kites%20in%20sky%2C%20coastal%20properties%20along%20beach%2C%20famous%20Bloubergstrand%20scenery%2C%20Cape%20Town%20landmark%20view&width=600&height=400&seq=blouberg8new&orientation=landscape"
                alt="Bloubergstrand Coastal Area"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local Services Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comprehensive Bloubergstrand Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Specialized painting solutions designed specifically for Bloubergstrand's unique coastal environment and windy conditions.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                  <i className={`${service.icon} text-black text-2xl`}></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <div className="text-yellow-600 font-semibold text-lg mb-4">{service.price}</div>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6" style={{backgroundColor: '#ffff00'}}>
                        <i className={`${feature.icon} text-black text-2xl`}></i>
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Painting Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Bloubergstrand Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A specialized approach designed for Bloubergstrand's challenging coastal conditions and wind exposure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paintingProcess.map((process, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 text-black rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                  {process.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{process.title}</h3>
                <p className="text-gray-600">{process.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us for Bloubergstrand */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Us for Bloubergstrand?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Unmatched expertise in Bloubergstrand's unique coastal painting challenges with proven solutions and satisfied customers.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-windy-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Wind Resistance</h3>
              <p className="text-gray-600">Specialized techniques and materials specifically developed to handle Bloubergstrand's strong coastal winds and sand abrasion challenges.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-map-pin-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Local Specialists</h3>
              <p className="text-gray-600">Deep understanding of Bloubergstrand's unique coastal painting requirements gained through years of specialized experience in the area.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-home-heart-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Holiday Home Experts</h3>
              <p className="text-gray-600">Specialized service for Bloubergstrand holiday homes and rental properties with flexible scheduling and tenant coordination.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-lg flex items-center justify-center mx-auto mb-4">
                <i className="ri-calendar-line text-2xl text-yellow-600"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Flexible Timing</h3>
              <p className="text-gray-600">Work around weather conditions and your Bloubergstrand property schedule with weekend and holiday availability when needed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Coastal Challenges Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bloubergstrand's Unique Coastal Challenges
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Environmental Factors We Address</h3>
              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-windy-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Persistent Strong Winds</h4>
                    <p className="text-gray-600">Bloubergstrand's famous winds, while perfect for windsurfing, create constant stress on paint finishes and require specialized flexible coatings.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-contrast-drop-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Sand Abrasion</h4>
                    <p className="text-gray-600">Wind-blown sand creates constant abrasive action that can quickly wear down standard paint finishes, requiring enhanced durability solutions.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-drop-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Salt Spray Exposure</h4>
                    <p className="text-gray-600">Direct exposure to ocean spray and salt-laden air requires marine-grade protective coatings to prevent corrosion and deterioration.</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-sun-line text-yellow-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Intense UV Exposure</h4>
                    <p className="text-gray-600">Bloubergstrand's open coastal location provides little natural UV protection, requiring specialized UV-resistant formulations for color stability.</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Bloubergstrand%20coastal%20environment%20showing%20strong%20winds%20with%20kites%20and%20windsurfers%2C%20Table%20Mountain%20view%2C%20sand%20effects%2C%20salt%20spray%20on%20beachfront%20properties%2C%20environmental%20challenges&width=600&height=500&seq=blouberg9new&orientation=portrait"
                alt="Bloubergstrand Coastal Challenges"
                className="w-full h-96 object-cover object-top rounded-xl shadow-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Recent Projects */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Recent Bloubergstrand Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcasing our expertise in Bloubergstrand coastal painting with recent successful projects across the area.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <img
                  src={project.image}
                  alt={project.title}
                  className="w-full h-64 object-cover object-top"
                />
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{project.title}</h3>
                  <p className="text-gray-600">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Bloubergstrand Client Reviews
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Hear from satisfied Bloubergstrand property owners who have experienced our specialized coastal painting services.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-xl p-8">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-lg"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">"{testimonial.text}"</p>
                <div className="flex items-center">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover mr-4"
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900">{testimonial.name}</h4>
                    <p className="text-gray-600 text-sm">{testimonial.property}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
            Ready to Paint Your Bloubergstrand Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact Cape Town's coastal painting specialists for Bloubergstrand. Our expert team understands the unique challenges of this windy coastal environment and is ready to provide you with a comprehensive consultation and detailed quote for your beachside property. Experience the difference that specialized wind and salt-resistant painting solutions make.
          </p>
          <div className="flex flex-col sm:flex-row gap-6">
            <a
              href="/contact"
              className="bg-teal-600 text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Free Quote
            </a>
          </div>
        </div>
      </section>

      <Footer />
      
      {/* Elfsight Popup */}
      <div className="elfsight-app-a8d09b4b-1f9c-47af-9c95-257e6e1aa6a7" data-elfsight-app-lazy></div>

      <a
        href="mailto:Get a quote"
        className="bg-black text-white px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
      >
        Get Quote
      </a>
    </div>
  );
}
